import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ApolloService } from '../../../../core/services/apollo.service';
import moment from 'moment';
import { Observable, of, switchMap, take } from 'rxjs';
import { Order } from 'src/app/order-module/shared/entities/order/order';
import { AssignmentStrategyCandidate } from '../../entities/assignmentStrategyCandidate/assignmentStrategyCandidate';
import { DateTimePeriod } from '../../entities/dateTimePeriod/dateTimePeriod';
import { TaskAssignmentInput } from '../../entities/taskAssignmentInput/taskAssignmentInput';
import * as fromGraphQl from './graphql';

export interface DueDateTimeDisplay {
  text: string;
  date: string;
  time?: string;
  dayOffset?: number;
  tooltip?: string;
}

/**
 * Tasks service.
 */
@Injectable()
export class TasksService {
  constructor(private apolloService: ApolloService,
    public datePipe: DatePipe,
    ) {}

  /**
   * Completes TAsk
   * @param id Task Id
   * @param protocol protocol zu Complete Task
   */
  public completeTask(taskId: string, formModelInput: any, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.COMPLETE_TASK_MUTATION,
      {
        taskId,
        formModelInput,
      },
      callback
    );
  }

  /**
   * Assign Task
   * @param id Task Id
   * @param protocol protocol zu Complete Task
   */
    public assignTask(taskAssignmentInput: TaskAssignmentInput, callback: any): void {
      this.apolloService.mutateAndExecute(
        fromGraphQl.ASSIGN_TASK_MUTATION,
        {
          taskAssignmentInput,
        },
        callback
      );
    }

  /**
   * Assign Tasks
   * @param taskAssignmentInputs array of TaskAssignmentInputs
   * @param callback callback function
   */
  public assignTasks(taskAssignmentInputs: TaskAssignmentInput[], callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.ASSIGN_TASKS_MUTATION,
      {
        taskAssignmentInputs,
      },
        callback
    );
  }

  /**
   * un Assign Task
   * @param id Task Id
   * @param protocol protocol zu Complete Task
   */
  public unassignTasks(taskIds: string[], callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UNASSIGN_TASKS_MUTATION,
      {
        taskIds,
      },
      callback
    );
  }

  /**
   * un Assign Task
   * @param id Task Id
   * @param protocol protocol zu Complete Task
   */
  public unassignTask(taskId: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UNASSIGN_TASK_MUTATION,
      {
        taskId,
      },
      callback
    );
  }

  public getAssignmentStrategyCandidates(taskId: string, strategyTypes?: string[]): Observable<AssignmentStrategyCandidate[]> {
    return this.apolloService.query<fromGraphQl.GetAssignmentStrategyCandidatesResponse>(
      fromGraphQl.GET_ASSIGNMENT_STRATEGY_CANDIDATES_QUERY, 
      { 
        taskId: taskId,
        strategyTypes: strategyTypes
      })
      .pipe(
        take(1),
        switchMap( (result) => {
          return of( AssignmentStrategyCandidate.createAssignmentStrategyCandidates(result.data.getAssignmentStrategyCandidates));
        })
      );
  }

public getDueDateTimeDisplay(dateTime: DateTimePeriod): DueDateTimeDisplay | undefined {
    if (!dateTime) {
      return undefined;
    }

    const currentYear = new Date().getFullYear();
    
    if (dateTime.modifier === 'BETWEEN') {
      const fromMoment = moment(dateTime.fromDateTime);
      const toMoment = moment(dateTime.toDateTime);
      const dayDifference = toMoment.diff(fromMoment, 'days');
      const fromYear = fromMoment.year();
      const dateFormat = fromYear === currentYear ? 'E dd.MM' : 'E dd.MM.yy';
      
      if (dateTime.precision === 'DATE_TIME') {
        const dateStr = this.datePipe.transform(dateTime.fromDateTime, dateFormat);
        const fromTime = this.datePipe.transform(dateTime.fromDateTime, 'HH:mm');
        const toTime = this.datePipe.transform(dateTime.toDateTime, 'HH:mm');
        const timeRange = `${fromTime}-${toTime}`;

        if (dayDifference > 0) {
          const toDateFormat = toMoment.year() === currentYear ? 'E dd.MM' : 'E dd.MM.yy';
          const toDateStr = this.datePipe.transform(dateTime.toDateTime, toDateFormat);
          const tooltip = `${dateStr} ${fromTime} - ${toDateStr} ${toTime}`;
          return { text: `${dateStr} ${timeRange} +${dayDifference}`, date: dateStr, time: timeRange, dayOffset: dayDifference, tooltip };
        }
        return { text: `${dateStr} ${timeRange}`, date: dateStr, time: timeRange };
      } else {
        // DATE precision
        const dateStr = this.datePipe.transform(dateTime.fromDateTime, dateFormat);
        if (dayDifference > 0) {
          const toDateFormat = toMoment.year() === currentYear ? 'E dd.MM' : 'E dd.MM.yy';
          const toDateStr = this.datePipe.transform(dateTime.toDateTime, toDateFormat);
          const tooltip = `${dateStr} - ${toDateStr}`;
          return { text: `${dateStr} +${dayDifference}`, date: dateStr, dayOffset: dayDifference, tooltip };
        }
        return { text: dateStr, date: dateStr };
      }
    } else {
      // BEFORE, AFTER, EXACT modifiers
      let modifierString = '';
      if (dateTime.modifier === 'BEFORE') {
        modifierString = 'vor ';
      } else if (dateTime.modifier === 'AFTER') {
        modifierString = 'nach ';
      }
      
      const year = new Date(dateTime.dateTime).getFullYear();
      const dateFormat = year === currentYear ? 'E dd.MM' : 'E dd.MM.yy';
      const dateStr = this.datePipe.transform(dateTime.dateTime, dateFormat);
      
      if (dateTime.precision === 'DATE_TIME') {
        const timeStr = this.datePipe.transform(dateTime.dateTime, 'HH:mm');
        return { text: `${modifierString}${dateStr} ${timeStr}`, date: `${modifierString}${dateStr}`, time: timeStr };
      } else {
        return { text: `${modifierString}${dateStr}`, date: `${modifierString}${dateStr}` };
      }
    }
  }

  public getDueDateTime(dateTime: DateTimePeriod): string | undefined {
    return this.getDueDateTimeDisplay(dateTime)?.text;
  }


    public getDeliveryDate(dateTime: DateTimePeriod) {
      if (dateTime){
        switch(dateTime?.modifier) {
         case 'BEFORE':
           return this.datePipe.transform(dateTime.dateTime, "dd.MM");
         case 'AFTER':
           return this.datePipe.transform(dateTime.dateTime, "dd.MM");
         case 'EXACT':
           return this.datePipe.transform(dateTime.dateTime, "dd.MM");
         case 'BETWEEN':
           return this.datePipe.transform(dateTime.fromDateTime, "dd.MM")
         default:
           return '-';
        }
       }
       return '-';

    }
    public getDeliveryTime(dateTime: DateTimePeriod){
      if (dateTime){
       switch(dateTime?.modifier) {
        case 'BEFORE':
          return 'vor ' + this.datePipe.transform(dateTime.dateTime, "HH:mm");
        case 'AFTER':
          return 'nach ' + this.datePipe.transform(dateTime.dateTime, "HH:mm");
        case 'EXACT':
          return this.datePipe.transform(dateTime.dateTime, "HH:mm");
        case 'BETWEEN':
          let a = moment(dateTime?.fromDateTime);
          let b = moment(dateTime?.toDateTime);
          let difference = b.diff(a, 'days');
          let dateString= this.datePipe.transform(dateTime.fromDateTime, "HH:mm") + '-'
          +this.datePipe.transform(dateTime.toDateTime, "HH:mm") ;
          dateString += (difference && difference>0)? ' +' + difference : '' ;
          return dateString;
        default:
          return '--:--';
       }
      }
      return '--:--';
    }

    public getDeliveryDateFull(dateTime: DateTimePeriod) {
      if (dateTime){
        switch(dateTime?.modifier) {
         case 'BEFORE':
           return this.datePipe.transform(dateTime.dateTime, "EEE, dd.MM.yy");
         case 'AFTER':
           return this.datePipe.transform(dateTime.dateTime, "EEE, dd.MM.yy");
         case 'EXACT':
           return this.datePipe.transform(dateTime.dateTime, "EEE, dd.MM.yy");
         case 'BETWEEN':
           return this.datePipe.transform(dateTime.fromDateTime, "EEE, dd.MM.yy")
         default:
           return '-';
        }
       }
       return '-';
    }

    public getCommentsNotesCount(order: Order) : number {
      return order?.note? 1 + order?.comments?.length  : order?.comments?.length;
    }
}
